import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useLoader } from "../../contexts/LoaderContext";
import { useLoading } from "../../contexts/LoadingContext";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import DashboardService, {
  DashboardData,
} from "../../services/DashboardService";
import rbacUtils from "../../utils/rbacUtils";
import "./Dashboard.scss";

// Import Material-UI icons
import SearchIcon from "@mui/icons-material/Search";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Checkbox from "@mui/material/Checkbox";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import TodayIcon from "@mui/icons-material/Today";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";

// Import Recharts for better charts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Dashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const { can } = usePermissions();
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [selectedJobs, setSelectedJobs] = useState<Set<number>>(new Set());
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [todayStats] = useState({
    jobsCreated: 24,
    jobsPending: 18,
    jobsCompleted: 47,
    jobsInProgress: 12,
  });
  const [liveJobStatus] = useState({
    total: 156,
    initiated: 23,
    assigned: 34,
    started: 18,
    inProgress: 12,
    completed: 47,
    cancelled: 22,
  });
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);
  const initialLoadDone = useRef<boolean>(false);
  const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refreshes

  // Job status colors mapping
  const JOB_STATUS_COLORS = {
    INITIATED: "#ffc107", // Yellow
    ASSIGNED: "#17a2b8", // Blue
    STARTED: "#fd7e14", // Orange
    IN_PROGRESS: "#fd7e14", // Orange
    COMPLETED: "#28a745", // Green
    CANCELLED: "#dc3545", // Red
  };

  // Chart data for Total Trips
  const tripsData = [
    { day: "Mon", pickup: 12000, drop: 8000 },
    { day: "Tue", pickup: 15000, drop: 10000 },
    { day: "Wed", pickup: 18000, drop: 12000 },
    { day: "Thu", pickup: 16000, drop: 11000 },
    { day: "Fri", pickup: 14000, drop: 9000 },
    { day: "Sat", pickup: 10000, drop: 7000 },
    { day: "Sun", pickup: 8000, drop: 5000 },
  ];

  // Chart data for Job Statuses
  const jobStatusData = [
    { status: "Initiated", count: 8500, color: "#ffc107" },
    { status: "Assigned", count: 12000, color: "#17a2b8" },
    { status: "In Progress", count: 6800, color: "#fd7e14" },
    { status: "Completed", count: 23573, color: "#28a745" },
  ];

  // Chart data for Total Containers
  const containersData = [
    { day: "Mon", "20 ft": 8000, "40 ft": 6000, "45 ft": 2000 },
    { day: "Tue", "20 ft": 10000, "40 ft": 7000, "45 ft": 2500 },
    { day: "Wed", "20 ft": 12000, "40 ft": 8000, "45 ft": 3000 },
    { day: "Thu", "20 ft": 11000, "40 ft": 7500, "45 ft": 2800 },
    { day: "Fri", "20 ft": 9000, "40 ft": 6500, "45 ft": 2200 },
    { day: "Sat", "20 ft": 7000, "40 ft": 5000, "45 ft": 1800 },
    { day: "Sun", "20 ft": 5000, "40 ft": 4000, "45 ft": 1500 },
  ];

  // Pie chart data for container distribution
  const containerPieData = [
    { name: "20 ft", value: 65000, color: "#007bff" },
    { name: "40 ft", value: 45000, color: "#6f42c1" },
    { name: "45 ft", value: 15800, color: "#20c997" },
  ];

  // Mock data for recent completed jobs
  const recentJobs = [
    {
      id: 1,
      jobNo: "832722",
      containerNo: "CMAU2345678",
      city: "40 HQ",
      truck: "KA 01 AB 1234",
      source: "NSFT",
      destination: "SPEEDY CFS",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 2,
      jobNo: "847936",
      containerNo: "OPFA9382419",
      city: "40 HQ",
      truck: "MH 12 CD 5678",
      source: "NSIGT",
      destination: "SPEEDY CFS",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 3,
      jobNo: "867276",
      containerNo: "NLGS4116155",
      city: "20 OT",
      truck: "DL 3C AF 4321",
      source: "SPEEDY CFS",
      destination: "NSIGT",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 4,
      jobNo: "874568",
      containerNo: "CBYL4611647",
      city: "20 FL",
      truck: "TN 45 GH 8765",
      source: "NSFT",
      destination: "SPEEDY CFS",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 5,
      jobNo: "812285",
      containerNo: "VVUS5400999",
      city: "40 HQ",
      truck: "AP 09 JK 2468",
      source: "SPEEDY CFS",
      destination: "BMCT",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 6,
      jobNo: "862406",
      containerNo: "WZDT7262195",
      city: "40 HQ",
      truck: "RJ 14 NO 9753",
      source: "SPEEDY CFS",
      destination: "BMCT",
      completedOn: "23/09/2024 03:57",
    },
    {
      id: 7,
      jobNo: "839939",
      containerNo: "BJNN1769062",
      city: "20 OT",
      truck: "UP 32 LM 1357",
      source: "SPEEDY CFS",
      destination: "BMCT",
      completedOn: "23/09/2024 03:57",
    },
  ];

  // Activity data
  const activityData = [
    { name: "Sharma Logistics", jobs: 3879, initials: "SL" },
    { name: "Gupta Freight", jobs: 3321, initials: "GF" },
    { name: "Singh Cargo", jobs: 1432, initials: "SC" },
    { name: "Rajesh Transporters", jobs: 3329, initials: "RT" },
    { name: "Mehta Movers", jobs: 1238, initials: "MM" },
    { name: "Desai Trucking", jobs: 3389, initials: "DT" },
    { name: "Khan Logistics", jobs: 3142, initials: "KL" },
    { name: "Nair Express", jobs: 3126, initials: "NE" },
    { name: "Verma Freight Solutions", jobs: 3759, initials: "VFS" },
    { name: "Bose Roadways", jobs: 4103, initials: "BR" },
    { name: "Goyal Transport", jobs: 1516, initials: "GT" },
    { name: "Reddy Cargo Carriers", jobs: 1301, initials: "RCC" },
    { name: "Chauhan Logistics", jobs: 3029, initials: "CL" },
  ];

  const fetchDashboardData = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      const now = Date.now();
      if (!forceRefresh && now - lastRefreshTime.current < REFRESH_COOLDOWN) {
        console.log("Refresh skipped - too soon since last refresh");
        if (!suppressLoader) {
          hideLoader();
        } else {
          loader.setLoading(false);
        }
        return;
      }
      lastRefreshTime.current = now;

      if (!userProfile) {
        console.log("No userProfile, returning");
        setLoading(false);
        initialLoadDone.current = true;
        if (!suppressLoader) {
          hideLoader();
        } else {
          loader.setLoading(false);
        }
        return;
      }

      try {
        if (!initialLoadDone.current && !suppressLoader) {
          setLoading(true);
        }

        if (suppressLoader) {
          // Auto-refresh in progress
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        console.log("Fetching dashboard data for user:", userProfile);
        const data = await DashboardService.getDashboardData(userProfile);
        console.log("Dashboard data received:", data);
        setDashboardData(data);
        setLastUpdated(new Date());
      } catch (error: any) {
        console.error("Error fetching dashboard data:", error);
        setError(error.message || "Failed to load dashboard data");
      } finally {
        if (!initialLoadDone.current) {
          setLoading(false);
          initialLoadDone.current = true;
        }

        // Auto-refresh completed

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [userProfile, showLoader, hideLoader, loader]
  );

  // Initial data fetch
  useEffect(() => {
    if (userProfile) {
      const fetchData = async () => {
        try {
          await fetchDashboardData(false, false);
        } finally {
          // No need to hide loader here as it's handled in fetchDashboardData
        }
      };

      fetchData();
    } else {
      const timer = setTimeout(() => {
        setLoading(false);
        initialLoadDone.current = true;
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [fetchDashboardData, userProfile]);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
    }

    if (userProfile) {
      autoRefreshInterval.current = setInterval(() => {
        fetchDashboardData(true, false);
      }, 30000);
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [fetchDashboardData, userProfile]);

  const handleJobSelection = (jobId: number) => {
    setSelectedJobs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const handleSelectAllJobs = () => {
    if (selectedJobs.size === recentJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(recentJobs.map((job) => job.id)));
    }
  };

  const handleActionMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    jobId: number
  ) => {
    setActionMenuAnchor(event.currentTarget);
    setActiveJobId(jobId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActiveJobId(null);
  };

  const handleActionClick = (action: string) => {
    console.log(`Action "${action}" clicked for job ${activeJobId}`);
    handleActionMenuClose();
  };

  console.log("Dashboard render state:", {
    loading,
    error,
    dashboardData,
    userProfile,
  });

  if (loading) {
    return <FullPageLoader />;
  }

  if (!userProfile) {
    return (
      <div className="dashboard-error">
        <div className="error-container">
          <h2>User Profile Not Available</h2>
          <p>
            Unable to load user profile. Please try refreshing the page or
            logging in again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Header */}
      {/* <div className="dashboard-header">
        <div className="header-left">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
        <div className="header-center">
          <div className="search-container">
            <SearchIcon className="search-icon" />
            <input type="text" placeholder="Search" className="search-input" />
          </div>
        </div>
        <div className="header-right">
          <button className="notification-btn">
            <NotificationsIcon />
          </button>
          <div className="user-profile">
            <Avatar className="user-avatar">
              {userProfile?.userFullName?.charAt(0) || "S"}
            </Avatar>
            <div className="user-info">
              <div className="user-name">
                {userProfile?.userFullName || "Soumya Ranjan"}
              </div>
              <div className="user-role">
                {userProfile?.role?.name
                  ?.replace("ROLE_", "")
                  .replace("_", " ") || "Admin"}
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Main Content */}
      <div className="dashboard-content">
        <div className="content-main">
          {/* Summary Cards */}
          {/* <div className="summary-cards">
            <SummaryCard
              title="Total Jobs"
              value="23,573"
              trend="up"
              trendValue="12.5%"
              icon={<AssignmentIcon />}
              color="#007bff"
            />
            <SummaryCard
              title="Transporters"
              value="523"
              progress={65}
              icon={<LocalShippingIcon />}
              color="#28a745"
            />
            <SummaryCard
              title="Distance Covered"
              value="1,05,351 Km"
              trend="down"
              trendValue="3.2%"
              icon={<LocationOnIcon />}
              color="#dc3545"
            />
          </div> */}

          {/* Today's Activity Section */}
          <div className="todays-activity-section">
            <div className="section-header">
              <h3>
                <TodayIcon className="section-icon" />
                Today's Activity Summary
              </h3>
              <span className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
            <div className="activity-cards">
              <TodayActivityCard
                title="Jobs Created"
                value={todayStats.jobsCreated}
                icon={<AssignmentIcon />}
                color="#007bff"
                trend="up"
                trendValue="+15%"
              />
              <TodayActivityCard
                title="Jobs Pending"
                value={todayStats.jobsPending}
                icon={<PendingIcon />}
                color="#ffc107"
                trend="neutral"
              />
              <TodayActivityCard
                title="Jobs In Progress"
                value={todayStats.jobsInProgress}
                icon={<PlayArrowIcon />}
                color="#fd7e14"
                trend="up"
                trendValue="+8%"
              />
              <TodayActivityCard
                title="Jobs Completed"
                value={todayStats.jobsCompleted}
                icon={<CheckCircleIcon />}
                color="#28a745"
                trend="up"
                trendValue="+22%"
              />
            </div>
          </div>

          {/* Live Job Status Section */}
          <div className="live-job-status-section">
            <div className="section-header">
              <h3>
                <ScheduleIcon className="section-icon" />
                Live Job Status Board
              </h3>
            </div>
            <div className="status-cards">
              <JobStatusCard
                status="INITIATED"
                count={liveJobStatus.initiated}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.INITIATED}
                icon={<AssignmentIcon />}
              />
              <JobStatusCard
                status="ASSIGNED"
                count={liveJobStatus.assigned}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.ASSIGNED}
                icon={<LocalShippingIcon />}
              />
              <JobStatusCard
                status="STARTED"
                count={liveJobStatus.started}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.STARTED}
                icon={<PlayArrowIcon />}
              />
              <JobStatusCard
                status="IN_PROGRESS"
                count={liveJobStatus.inProgress}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.IN_PROGRESS}
                icon={<ScheduleIcon />}
              />
              <JobStatusCard
                status="COMPLETED"
                count={liveJobStatus.completed}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.COMPLETED}
                icon={<CheckCircleIcon />}
              />
              <JobStatusCard
                status="CANCELLED"
                count={liveJobStatus.cancelled}
                total={liveJobStatus.total}
                color={JOB_STATUS_COLORS.CANCELLED}
                icon={<StopIcon />}
              />
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Total Trips</h3>
                <div className="chart-controls">
                  <select className="chart-select">
                    <option>vs. Last Week</option>
                    <option>vs. Last Month</option>
                    <option>vs. Last Year</option>
                  </select>
                </div>
              </div>
              <div className="chart-content">
                <div className="chart-value">
                  <span className="value">15,334</span>
                  <div className="trend-indicator down">
                    <TrendingDownIcon />
                    <span>3.2%</span>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color pickup"></div>
                    <span>Pickup</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color drop"></div>
                    <span>Drop</span>
                  </div>
                </div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={tripsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="pickup" fill="#007bff" />
                      <Bar dataKey="drop" fill="#20c997" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* <div className="chart-card job-status-chart">
              <div className="chart-header">
                <h3>Job Status Distribution</h3>
                <div className="chart-controls">
                  <select className="chart-select">
                    <option>vs. Last Week</option>
                    <option>vs. Last Month</option>
                    <option>vs. Last Year</option>
                  </select>
                </div>
              </div>
              <div className="chart-content">
                <div className="chart-value">
                  <span className="value">23,573</span>
                  <div className="trend-indicator up">
                    <TrendingUpIcon />
                    <span>12.5%</span>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color initiated"></div>
                    <span>Initiated</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color assigned"></div>
                    <span>Assigned</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color in-progress"></div>
                    <span>In Progress</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color completed"></div>
                    <span>Completed</span>
                  </div>
                </div>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={jobStatusData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#007bff">
                        {jobStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div> */}

            <div className="chart-card">
              <div className="chart-header">
                <h3>Total Containers</h3>
                <div className="chart-controls">
                  <select className="chart-select">
                    <option>vs. Last Week</option>
                    <option>vs. Last Month</option>
                    <option>vs. Last Year</option>
                  </select>
                </div>
              </div>
              <div className="chart-content">
                <div className="chart-value">
                  <span className="value">18,276</span>
                  <div className="trend-indicator up">
                    <TrendingUpIcon />
                    <span>2.5%</span>
                  </div>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color container-20"></div>
                    <span>20 ft</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color container-40"></div>
                    <span>40 ft</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color container-45"></div>
                    <span>45 ft</span>
                  </div>
                </div>
                <div className="charts-container">
                  <div className="area-chart-container">
                    <ResponsiveContainer width="100%" height={150}>
                      <AreaChart data={containersData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Area
                          type="monotone"
                          dataKey="20 ft"
                          stackId="1"
                          stroke="#007bff"
                          fill="#007bff"
                        />
                        <Area
                          type="monotone"
                          dataKey="40 ft"
                          stackId="1"
                          stroke="#6f42c1"
                          fill="#6f42c1"
                        />
                        <Area
                          type="monotone"
                          dataKey="45 ft"
                          stackId="1"
                          stroke="#20c997"
                          fill="#20c997"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  {/* <div className="pie-chart-container">
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie
                          data={containerPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={60}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {containerPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Completed Jobs Table */}
          <div className="recent-jobs-section">
            <h3>Recent Completed Jobs</h3>
            <div className="jobs-table-container">
              <table className="jobs-table">
                <thead>
                  <tr>
                    <th>
                      <Checkbox
                        checked={selectedJobs.size === recentJobs.length}
                        indeterminate={
                          selectedJobs.size > 0 &&
                          selectedJobs.size < recentJobs.length
                        }
                        onChange={handleSelectAllJobs}
                      />
                    </th>
                    <th>Sl No.</th>
                    <th>Job No</th>
                    <th>Container No</th>
                    <th>City</th>
                    <th>Truck</th>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Completed On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.map((job, index) => (
                    <tr key={job.id}>
                      <td>
                        <Checkbox
                          checked={selectedJobs.has(job.id)}
                          onChange={() => handleJobSelection(job.id)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td className="job-link">{job.jobNo}</td>
                      <td>{job.containerNo}</td>
                      <td>{job.city}</td>
                      <td>{job.truck}</td>
                      <td>{job.source}</td>
                      <td>{job.destination}</td>
                      <td>{job.completedOn}</td>
                      <td>
                        <button
                          className="more-btn"
                          onClick={(e) => handleActionMenuOpen(e, job.id)}
                        >
                          <MoreVertIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="dashboard-sidebar">
          {/* Top Transporters */}
          <div className="top-transporters-section">
            <h3>Top Transporters</h3>
            <div className="transporter-cards">
              <div className="transporter-card">
                <div className="transporter-logo">
                  <div className="logo-circle">
                    <span>NT</span>
                  </div>
                </div>
                <div className="transporter-info">
                  <h4>Narendra Transporter</h4>
                  <div className="transporter-stats">
                    <span className="stat-label">Jobs Completed</span>
                    <span className="stat-value">5728</span>
                  </div>
                </div>
              </div>
              <div className="transporter-card">
                <div className="transporter-logo">
                  <div className="logo-circle">
                    <span>PT</span>
                  </div>
                </div>
                <div className="transporter-info">
                  <h4>Patel Transport Co</h4>
                  <div className="transporter-stats">
                    <span className="stat-label">Jobs Completed</span>
                    <span className="stat-value">4997</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Section */}
          <div className="activity-section">
            <div className="activity-header">
              <h3>Activity</h3>
              <a href="#" className="view-all-link">
                View All
              </a>
            </div>
            <div className="activity-list">
              {activityData.map((transporter, index) => (
                <div key={index} className="activity-item">
                  <Avatar className="activity-avatar">
                    {transporter.initials}
                  </Avatar>
                  <div className="activity-content">
                    <div className="activity-name">{transporter.name}</div>
                    <div className="activity-jobs">
                      Total Jobs: {transporter.jobs}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        className="action-menu"
      >
        <MenuItem onClick={() => handleActionClick("view")}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick("edit")}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Job</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick("download")}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Report</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick("share")}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleActionClick("delete")}
          className="delete-action"
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string;
  trend?: "up" | "down";
  trendValue?: string;
  progress?: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, trend, trendValue, progress, icon, color }) => {
  return (
    <div className="summary-card">
      <div className="card-header">
        <div className="card-icon" style={{ backgroundColor: color }}>
          {icon}
        </div>
        {trend && (
          <div className={`trend-indicator ${trend}`}>
            {trend === "up" ? <TrendingUpIcon /> : <TrendingDownIcon />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className="card-content">
        <h3 className="card-title">{title}</h3>
        <div className="card-value">{value}</div>
        {progress !== undefined && (
          <div className="progress-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%`, backgroundColor: color }}
              ></div>
            </div>
            <span className="progress-text">{progress}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Today Activity Card Component
const TodayActivityCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  trend?: "up" | "down" | "neutral";
  trendValue?: string;
}> = ({ title, value, icon, color, trend, trendValue }) => {
  return (
    <div className="today-activity-card">
      <div className="card-header">
        <div className="card-icon" style={{ backgroundColor: color }}>
          {icon}
        </div>
        {trend && trend !== "neutral" && (
          <div className={`trend-indicator ${trend}`}>
            {trend === "up" ? <TrendingUpIcon /> : <TrendingDownIcon />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className="card-content">
        <h3 className="card-title">{title}</h3>
        <div className="card-value">{value}</div>
        <div className="card-subtitle">Today</div>
      </div>
    </div>
  );
};

// Job Status Card Component
const JobStatusCard: React.FC<{
  status: string;
  count: number;
  total: number;
  color: string;
  icon: React.ReactNode;
}> = ({ status, count, total, color, icon }) => {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  const statusDisplayNames: { [key: string]: string } = {
    INITIATED: "Initiated",
    ASSIGNED: "Assigned",
    STARTED: "Started",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
  };

  return (
    <div
      className="job-status-card"
      style={{ "--status-color": color } as React.CSSProperties}
    >
      <div className="card-header">
        <div className="status-icon" style={{ color }}>
          {icon}
        </div>
        <div className="status-percentage" style={{ color }}>
          {percentage}%
        </div>
      </div>
      <div className="card-content">
        <h3 className="status-title" style={{ color }}>
          {statusDisplayNames[status] || status}
        </h3>
        <div className="status-count">{count}</div>
        <div className="status-total">of {total} total</div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
